import { getAuthHeaders } from './auth';
import type {
  UnifiedLead,
  LeadNote,
  LeadStatus,
  LeadFilters,
  DashboardStats,
  LeadsResponse,
} from './types';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// --- PIXEL BEACON: server-side capture via <img> tag ---
//
// This is the most reliable lead-capture path. It bypasses CORS preflight,
// fetch, sendBeacon, JSON parsing, Turnstile, and every JS error path. The
// browser fires the GET request the moment the <img> src is set, even if the
// page is unloading or JS has thrown.
//
// We use it on every form-field blur AND on submit, so the data is in the
// database BEFORE the user even clicks the submit button.

let __pixelSessionId: string | null = null;

/** Start a fresh partial-capture session after a lead has been fully submitted. */
export function rotatePixelSessionId(): void {
  __pixelSessionId = null;
  try { sessionStorage.removeItem('hola_pixel_sid'); } catch { /* ignore */ }
}

export function getPixelSessionId(): string {
  if (__pixelSessionId) return __pixelSessionId;
  try {
    const k = 'hola_pixel_sid';
    let sid: string | null = sessionStorage.getItem(k);
    if (!sid) {
      sid = (typeof crypto !== 'undefined' && (crypto as any).randomUUID)
        ? (crypto as any).randomUUID() as string
        : 'p-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
      sessionStorage.setItem(k, sid as string);
    }
    __pixelSessionId = sid as string;
    return __pixelSessionId;
  } catch {
    __pixelSessionId = 'p-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    return __pixelSessionId;
  }
}

/**
 * Fire-and-forget capture of partial form data via a 1x1 image pixel.
 * Call this on every form-field onBlur and on submit.
 */
export function pixelCaptureLead(partial: {
  name?: string;
  email?: string;
  phone?: string;
  country?: string;
  country_code?: string;
  lead_type?: 'contact' | 'eligibility';
  source_page?: string;
  source_form?: string;
}): void {
  try {
    if (typeof document === 'undefined') return;
    if (!partial) return;
    // Skip if all key fields empty (page-load noise)
    const hasData = !!(partial.name || partial.email || partial.phone);
    if (!hasData) return;
    const params = new URLSearchParams();
    params.set('sid', getPixelSessionId());
    if (partial.name) params.set('name', partial.name.slice(0, 100));
    if (partial.email) params.set('email', partial.email.slice(0, 200));
    if (partial.phone) params.set('phone', partial.phone.slice(0, 30));
    if (partial.country) params.set('country', partial.country.slice(0, 100));
    if (partial.country_code) params.set('cc', partial.country_code.slice(0, 8));
    if (partial.lead_type) params.set('lt', partial.lead_type);
    if (partial.source_page) params.set('sp', partial.source_page.slice(0, 200));
    if (partial.source_form) params.set('sf', partial.source_form.slice(0, 100));
    params.set('t', String(Date.now())); // cache-buster
    const url = `${API_BASE}/api/leads/ingest?${params.toString()}`;
    const img = new Image(1, 1);
    img.alt = '';
    img.style.position = 'absolute';
    img.style.left = '-9999px';
    img.style.opacity = '0';
    img.referrerPolicy = 'no-referrer-when-downgrade';
    img.src = url;
  } catch {
    // never throw from a fire-and-forget capture
  }
}

// --- Fetch all leads (paginated, filtered) ---

export async function fetchAllLeads(filters: LeadFilters, page = 1, limit = 100): Promise<UnifiedLead[]> {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.status !== 'All') params.set('status', filters.status);
  if (filters.sourcePage) params.set('source_page', filters.sourcePage);
  if (filters.dateFrom) params.set('date_from', filters.dateFrom);
  if (filters.dateTo) params.set('date_to', filters.dateTo);
  params.set('page', String(page));
  params.set('limit', String(limit));

  try {
    const res = await fetch(`${API_BASE}/api/admin/leads?${params}`, {
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    });
    if (!res.ok) return [];
    const data: LeadsResponse = await res.json();
    return data.leads;
  } catch {
    console.error('Error fetching leads');
    return [];
  }
}

// --- Update lead status ---

export async function updateLeadStatus(id: number, status: LeadStatus, remark?: string, uid?: string): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/api/admin/leads/${id}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ status, remark, uid }),
    });
    return res.ok;
  } catch {
    console.error('Error updating status');
    return false;
  }
}

// --- Notes ---

export async function fetchLeadNotes(leadId: number): Promise<LeadNote[]> {
  try {
    const res = await fetch(`${API_BASE}/api/admin/leads/${leadId}/notes`, {
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    });
    if (!res.ok) return [];
    const data = await res.json();
    return data.notes || [];
  } catch {
    console.error('Error fetching notes');
    return [];
  }
}

export async function addLeadNote(leadId: number, note: string, createdBy?: string | number): Promise<LeadNote | null> {
  try {
    const res = await fetch(`${API_BASE}/api/admin/leads/${leadId}/note`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ note, created_by: createdBy ? String(createdBy) : null }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.note || null;
  } catch {
    console.error('Error adding note');
    return null;
  }
}

// --- Dashboard stats ---

export async function fetchDashboardStats(): Promise<DashboardStats> {
  const empty: DashboardStats = { totalLeads: 0, newToday: 0, callDone: 0, notResponding: 0, addedInCRM: 0, bySource: {}, byStatus: {} };
  try {
    const res = await fetch(`${API_BASE}/api/admin/stats`, {
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    });
    if (!res.ok) return empty;
    return await res.json();
  } catch {
    return empty;
  }
}

// --- Polling (replaces Supabase Realtime) ---

export function startPolling(callback: () => void, intervalMs = 10000): () => void {
  const id = setInterval(callback, intervalMs);
  return () => clearInterval(id);
}

// --- CSV Export ---

// NEW-01 FIX: Sanitize CSV cells to prevent formula injection (=, +, -, @, tab, CR)
function sanitizeCSVCell(value: string): string {
  const s = String(value).replace(/"/g, '""');
  if (/^[=+\-@\t\r]/.test(s)) return `"'${s}"`;
  return `"${s}"`;
}

export function exportLeadsToCSV(leads: UnifiedLead[]): void {
  const headers = [
    'Date', 'Name', 'Country Code', 'Phone', 'Email', 'Type', 'Source Page', 'Source Form',
    'Country', 'Score', 'Recommendation', 'Status', 'Remark', 'UID', 'Called Date', 'Message'
  ];

  const rows = leads.map((lead) => [
    new Date(lead.created_at).toLocaleDateString(),
    lead.name,
    lead.country_code || '',
    lead.phone,
    lead.email,
    lead.lead_type,
    lead.source_page || '',
    lead.source_form || '',
    lead.country || '',
    lead.score?.toString() || '',
    lead.visa_recommendation || '',
    lead.status,
    lead.remark || '',
    lead.uid || '',
    lead.called_date || '',
    lead.message || '',
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.map((cell) => sanitizeCSVCell(cell)).join(',')),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `hola-world-leads-${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

// --- Server-side CSV Export with optional date range ---

export async function exportLeadsFromServer(from?: string, to?: string): Promise<void> {
  try {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString();
    const url = `${API_BASE}/api/admin/export${qs ? '?' + qs : ''}`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) throw new Error('Export failed');
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `hola-world-leads-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(blobUrl);
  } catch {
    console.error('Failed to export from server');
  }
}

// --- INSTANT CAPTURE (call from onClick / onSubmit FIRST LINE) ---
//
// This is the absolute earliest point we can capture a lead. It runs BEFORE
// any validation, honeypot check, Turnstile check, or async work. Even if the
// form handler later returns early, throws, or the user closes the tab, the
// data is already saved in localStorage AND a sendBeacon has been fired to
// the server. The only requirement: name OR email must be non-empty.
//
// This guarantees that any time a user enters their details and clicks Submit,
// the data is captured — full stop.
export function captureLeadInstantly(partial: Partial<LeadPayload> & { name?: string; email?: string; phone?: string }): void {
  try {
    if (!partial || (!partial.name && !partial.email && !partial.phone)) return;
    const payload: LeadPayload = {
      submission_id: partial.submission_id,
      session_id: partial.session_id,
      button_name: partial.button_name,
      cta_name: partial.cta_name,
      cta_page: partial.cta_page,
      name: partial.name || '',
      email: partial.email || '',
      phone: partial.phone || '',
      message: partial.message,
      country: partial.country,
      country_code: partial.country_code,
      lead_type: partial.lead_type || 'contact',
      source_page: partial.source_page,
      source_form: partial.source_form,
      form_position: partial.form_position,
      website_url: partial.website_url,
      company_website: partial.company_website,
      turnstileToken: partial.turnstileToken,
      _formLoadedAt: partial._formLoadedAt,
    };
    // localStorage only. submitLead() sends it (keepalive fetch survives tab close),
    // and retryPendingLeads() replays it if the submit never completed. Sending a
    // beacon here as well produced a second lead for every submission.
    queueLocal(payload);
  } catch {
    // Never throw from instant-capture; the form submit must keep working.
  }
}

// --- Submit lead (public) ---
//
// GUARANTEE: every submission is captured the moment Submit is pressed and is never lost.
//   1. The payload is written to localStorage BEFORE any network call (synchronous).
//   2. We attempt a normal fetch POST. On success, the entry is removed from localStorage.
//   3. If fetch fails or times out, we fall back to navigator.sendBeacon — survives even a
//      page close / navigation away.
//   4. Any unsent entries left in localStorage are auto-replayed on next page load
//      (see retryPendingLeads()).
//   5. The server itself also writes every arrival to S3 immediately (raw_arrival), so even
//      if Lambda processing fails after receiving the request, the data is captured there.

const PENDING_LEADS_KEY = 'hola_pending_leads_v1';
const SUBMIT_TIMEOUT_MS = 8000;

type LeadPayload = {
  /** One id per submit attempt; the server and CRM use it to drop repeats of the same submit. */
  submission_id?: string;
  /** Partial-capture session id, so the server can retire the partial record. */
  session_id?: string;
  /** Label of the button that submitted the form. */
  button_name?: string;
  /** Last call-to-action the visitor clicked before reaching the form. */
  cta_name?: string;
  cta_page?: string;
  name: string;
  email: string;
  phone: string;
  message?: string;
  country?: string;
  country_code?: string;
  lead_type: 'contact' | 'eligibility';
  source_page?: string;
  source_form?: string;
  form_position?: string;
  website_url?: string;
  company_website?: string;
  turnstileToken?: string;
  _formLoadedAt?: number;
  delivery_channel?: 'web' | 'beacon' | 'retry';
};

type PendingLead = {
  id: string;          // local UUID for dedupe (independent of server id)
  queued_at: string;
  payload: LeadPayload;
};

export function newSubmissionId(): string {
  return safeUUID();
}

function safeUUID(): string {
  try {
    if (typeof crypto !== 'undefined' && (crypto as any).randomUUID) return (crypto as any).randomUUID();
  } catch {}
  return 'l-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
}

function readPending(): PendingLead[] {
  try {
    const raw = localStorage.getItem(PENDING_LEADS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writePending(list: PendingLead[]) {
  try {
    localStorage.setItem(PENDING_LEADS_KEY, JSON.stringify(list));
  } catch {
    // localStorage may be unavailable (private mode quotas etc.) — ignore.
  }
}

function queueLocal(payload: LeadPayload): string {
  const localId = payload.submission_id || safeUUID();
  const list = readPending().filter((p) => p.id !== localId);
  list.push({ id: localId, queued_at: new Date().toISOString(), payload });
  writePending(list);
  return localId;
}

function dequeueLocal(localId: string) {
  const list = readPending().filter((p) => p.id !== localId);
  writePending(list);
}

function sendBeaconFallback(payload: LeadPayload): boolean {
  try {
    if (typeof navigator === 'undefined' || !navigator.sendBeacon) return false;
    const tagged = { ...payload, delivery_channel: 'beacon' as const };
    const blob = new Blob([JSON.stringify(tagged)], { type: 'application/json' });
    return navigator.sendBeacon(`${API_BASE}/api/leads`, blob);
  } catch {
    return false;
  }
}

async function fetchWithTimeout(url: string, init: RequestInit, timeoutMs: number): Promise<Response> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: ctrl.signal });
  } finally {
    clearTimeout(timer);
  }
}

export async function submitLead(input: LeadPayload): Promise<{ success: boolean; id?: any; scoreToken?: string; error?: string; queued?: boolean }> {
  const leadData: LeadPayload = {
    ...input,
    submission_id: input.submission_id || safeUUID(),
    session_id: input.session_id || getPixelSessionId(),
  };
  // STEP 1: Capture in browser immediately. This survives network failure, tab close,
  // browser crash, anything client-side. Replaces the instant-capture entry for this submit.
  const localId = queueLocal(leadData);

  // STEP 2: Try normal fetch with timeout.
  const taggedLead = { ...leadData, delivery_channel: leadData.delivery_channel || 'web' as const };
  try {
    const res = await fetchWithTimeout(`${API_BASE}/api/leads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(taggedLead),
      keepalive: true, // ensure delivery even if user navigates away
    }, SUBMIT_TIMEOUT_MS);

    let data: any = {};
    try { data = await res.json(); } catch { /* response body may be empty */ }

    if (!res.ok) {
      // Server rejected with a hard error. Keep entry in localStorage so it can be
      // inspected/retried (only 4xx with explicit reason should normally land here).
      return { success: false, error: data?.error || `Submission failed (HTTP ${res.status})` };
    }

    // Server accepted — safe to remove the local copy.
    dequeueLocal(localId);
    rotatePixelSessionId();
    return { success: true, id: data.id, scoreToken: data.scoreToken, queued: data.queued };
  } catch (err) {
    // Network error / timeout / abort — try sendBeacon as a last-resort delivery channel.
    const beaconOk = sendBeaconFallback(leadData);
    if (beaconOk) {
      // Beacon was queued by the browser. We can't read the server response so we keep
      // the local copy until the next page load retry confirms delivery (or just leave
      // it — duplicates are tolerated on the server side via duplicate_recent flag).
      return { success: true, queued: true, error: undefined };
    }
    // Beacon also unavailable. Keep in localStorage and report queued state.
    return {
      success: false,
      queued: true,
      error: 'You appear to be offline. Your details are saved on this device and will be sent automatically when you reconnect.',
    };
  }
}

// Auto-replay any pending leads that didn't get acknowledged by the server.
// Call this on app boot.
export async function retryPendingLeads(): Promise<void> {
  // Entries from the old instant-capture beacon were already delivered by the beacon;
  // replaying them created duplicates.
  const all = readPending();
  const pending = all.filter((p) => p.payload?.submission_id || p.payload?.delivery_channel !== 'beacon');
  if (pending.length !== all.length) writePending(pending);
  if (pending.length === 0) return;
  for (const entry of pending) {
    try {
      const tagged = { ...entry.payload, delivery_channel: 'retry' as const };
      const res = await fetchWithTimeout(`${API_BASE}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tagged),
        keepalive: true,
      }, SUBMIT_TIMEOUT_MS);
      if (res.ok) dequeueLocal(entry.id);
      // If !ok, leave in queue — we'll try again on next boot.
    } catch {
      // Still offline — leave in queue.
      break;
    }
  }
}

// --- Update lead score (requires scoreToken from lead creation) ---

export async function updateLeadScore(leadId: number, score: number, visa_recommendation: string, scoreToken: string): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/api/leads/${leadId}/score`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ score, visa_recommendation, scoreToken }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

// --- Admin User Management ---

export interface AdminListUser {
  id: number;
  name: string;
  email: string;
  role: string;
  is_active: number;
  created_at: string;
}

export async function fetchAdminUsers(): Promise<AdminListUser[]> {
  try {
    const res = await fetch(`${API_BASE}/api/admin/users`, {
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    });
    if (!res.ok) return [];
    return await res.json();
  } catch {
    return [];
  }
}

export async function createAdminUser(data: { name: string; email: string; password: string; role?: string }): Promise<{ success: boolean; error?: string; user?: AdminListUser }> {
  try {
    const res = await fetch(`${API_BASE}/api/admin/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) return { success: false, error: json.error || 'Failed to create user' };
    return { success: true, user: json };
  } catch {
    return { success: false, error: 'Network error' };
  }
}

export async function changeAdminPassword(userId: number, newPassword: string): Promise<{ success: boolean; error?: string }> {
  try {
    const res = await fetch(`${API_BASE}/api/admin/users/${userId}/password`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ newPassword }),
    });
    const json = await res.json();
    if (!res.ok) return { success: false, error: json.error || 'Failed to change password' };
    return { success: true };
  } catch {
    return { success: false, error: 'Network error' };
  }
}

export async function deleteAdminUser(userId: number): Promise<{ success: boolean; error?: string }> {
  try {
    const res = await fetch(`${API_BASE}/api/admin/users/${userId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    });
    const json = await res.json();
    if (!res.ok) return { success: false, error: json.error || 'Failed to delete user' };
    return { success: true };
  } catch {
    return { success: false, error: 'Network error' };
  }
}

export async function updateAdminUser(userId: number, data: { name?: string; email?: string; role?: string }): Promise<{ success: boolean; error?: string }> {
  try {
    const res = await fetch(`${API_BASE}/api/admin/users/${userId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) return { success: false, error: json.error || 'Failed to update user' };
    return { success: true };
  } catch {
    return { success: false, error: 'Network error' };
  }
}

export async function toggleAdminUserActive(userId: number, isActive: boolean): Promise<{ success: boolean; error?: string }> {
  try {
    const res = await fetch(`${API_BASE}/api/admin/users/${userId}/active`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ is_active: isActive ? 1 : 0 }),
    });
    const json = await res.json();
    if (!res.ok) return { success: false, error: json.error || 'Failed to update user status' };
    return { success: true };
  } catch {
    return { success: false, error: 'Network error' };
  }
}

export interface AdminSiteSettings {
  maintenance_mode: boolean;
  updated_at?: string | null;
  updated_by?: string | null;
}

export async function fetchAdminSiteSettings(): Promise<{ success: boolean; settings?: AdminSiteSettings; error?: string }> {
  try {
    const res = await fetch(`${API_BASE}/api/admin/settings`, {
      headers: { ...getAuthHeaders() },
    });
    const json = await res.json();
    if (!res.ok) return { success: false, error: json.error || 'Failed to fetch settings' };
    return {
      success: true,
      settings: {
        maintenance_mode: !!json.maintenance_mode,
        updated_at: json.updated_at,
        updated_by: json.updated_by,
      },
    };
  } catch {
    return { success: false, error: 'Network error' };
  }
}

export async function setMaintenanceMode(enabled: boolean): Promise<{ success: boolean; settings?: AdminSiteSettings; error?: string }> {
  try {
    const res = await fetch(`${API_BASE}/api/admin/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ maintenance_mode: enabled }),
    });
    const json = await res.json();
    if (!res.ok) return { success: false, error: json.error || 'Failed to update maintenance mode' };
    return {
      success: true,
      settings: {
        maintenance_mode: !!json.maintenance_mode,
        updated_at: json.updated_at,
        updated_by: json.updated_by,
      },
    };
  } catch {
    return { success: false, error: 'Network error' };
  }
}
